import type { FC, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";

import { RangeFixedValues } from "@similarweb/ui-components";

import type { IRange } from "app/pages/sales-intelligence/types/range";

import { formatPercent } from "../../utils/numberFormatters";
import { getFormattedRangeValue } from "../../utils/rangeValueFormatters";

import { StyledRangeFilter } from "./styles";

interface IRangeSliderProps {
    name?: string;
    title?: ReactNode;
    options: readonly number[];
    value: Partial<IRange<number>> | undefined;
    onAfterChange: (value: Partial<IRange<number>>) => void;
    isDisabled?: boolean;
}

const RangeSlider: FC<IRangeSliderProps> = ({ name, title, options, value, onAfterChange, isDisabled }) => {
    const [sliderValue, setSliderValue] = useState<Partial<IRange<number>>>();

    useEffect(() => {
        setSliderValue(value);
    }, [value]);

    const [min, max] = [options[0], options[options.length - 1]];

    const currentValue = useMemo<Partial<IRange<number>>>(
        () => ({
            from: sliderValue?.from ?? min,
            to: sliderValue?.to ?? max,
        }),
        [sliderValue, min, max],
    );

    const isInitialState = useMemo(
        () => (sliderValue?.from ?? min) === min && (sliderValue?.to ?? max) === max,
        [sliderValue, min, max],
    );

    const description = useMemo(
        () => getFormattedRangeValue(formatPercent, options, isInitialState ? currentValue : sliderValue),
        [sliderValue, currentValue, isInitialState, options],
    );

    const formatChangedValue = ([from, to]: readonly number[]): Partial<IRange<number>> => {
        return { from: from === min ? undefined : from, to: to === max ? undefined : to };
    };

    const handleChange = (range: number[]): void => {
        setSliderValue(formatChangedValue(range));
    };

    const handleAfterChange = (range: number[]): void => {
        onAfterChange(formatChangedValue(range));
    };

    return (
        <StyledRangeFilter isInitialState={isInitialState}>
            <RangeFixedValues
                name={name}
                title={title}
                pushable={1}
                value={[currentValue.from, currentValue.to]}
                allowCross={false}
                onChange={handleChange}
                fixedValues={options}
                onAfterChange={handleAfterChange}
                description={description}
                disabled={isDisabled}
            />
        </StyledRangeFilter>
    );
};

export default RangeSlider;
